import { checkBAID, getDistributorInformation } from '@/core/service/service'
import { useAuthToken, saveDistributorData, saveCustomerInfo } from '@/core/hooks/useStorage'
import { PageContainer } from '@/core/components'
import {
    distributorCustomerInformationAtom,
    distributorErrorAtom,
    distributorIsLoadingAtom,
    distributorRecordsAtom,
    distributorSearchValueAtom,
    TCheckBAIDResponse,
    TGetDistributorInformationResponse
} from '@/core/store/distributor.store'
import { useAtom, useSetAtom } from 'jotai'
import {
    Button,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import { ChangeEvent, KeyboardEvent, useCallback } from 'react'
import { useNavigate } from 'react-router'

const DistributorLayout = () => {
    const navigate = useNavigate()
    const token = useAuthToken()
    
    const [searchValue, setSearchValue] = useAtom(distributorSearchValueAtom)
    const [isLoading, setIsLoading] = useAtom(distributorIsLoadingAtom)
    const [errorMessage, setErrorMessage] = useAtom(distributorErrorAtom)
    const [distributorRecords, setDistributorRecords] = useAtom(distributorRecordsAtom)
    const setDistributorCustomerInfo = useSetAtom(distributorCustomerInformationAtom)

    const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/[^0-9]/g, '')
        setSearchValue(value)
        setErrorMessage('')
    }, [setSearchValue, setErrorMessage])

    const handleSearch = useCallback(async () => {
        if (!searchValue) {
            setErrorMessage('Vui lòng nhập số BA.')
            return
        }

        if (!token) {
            setErrorMessage('Error: Token not found')
            return
        }

        try {
            setIsLoading(true)
            setErrorMessage('')
            
            const response = await checkBAID({ baid: searchValue })
            const data = response.data as TCheckBAIDResponse

            if (!data?.baid) {
                setDistributorRecords([])
                setErrorMessage('Khách hàng không tìm thấy')
                return
            }

            const record: TCheckBAIDResponse = {
                baid: data.baid,
                name: data.name ?? '-',
                status: data.status ?? 'unknown',
                joinDate: data.joinDate ?? '-',
                type: data.type ?? '-',
                verify: data.verify ?? '-'
            }
            
            saveDistributorData(record)
            setDistributorRecords([record])
        } catch {
            setDistributorRecords([])
            setErrorMessage('Khách hàng không tìm thấy')
        } finally {
            setIsLoading(false)
        }
    }, [searchValue, token, setErrorMessage, setIsLoading, setDistributorRecords])

    const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    }, [handleSearch])

    const handleViewInformation = useCallback(async (baid: string) => {
        if (!token) {
            setErrorMessage('Error: Token not found')
            return
        }

        setIsLoading(true)

        try {
            const response = await getDistributorInformation({ distid: baid, cstoken: token })
            const data = response.data as TGetDistributorInformationResponse

            if (data.status === 'success') {
                setDistributorCustomerInfo(data)
                saveCustomerInfo(data)
                navigate('/viewba-details', { replace: true })
            } else {
                setErrorMessage('Error fetching distributor information')
            }
        } catch (error) {
            console.error('Error fetching distributor information:', error)
            setErrorMessage('Error fetching distributor information')
        } finally {
            setIsLoading(false)
        }
    }, [token, navigate, setIsLoading, setErrorMessage, setDistributorCustomerInfo])

    const renderTableContent = () => {
        if (distributorRecords.length === 0) {
            return (
                <TableRow sx={{ backgroundColor: 'rgba(0,0,0,.05)' }}>
                    <TableCell colSpan={4} sx={{ color: 'text.secondary', borderRight: '1px solid #dee2e6' }}>
                        {isLoading ? 'กำลังค้นหาข้อมูล...' : 'ยังไม่มีข้อมูลแสดง'}
                    </TableCell>
                </TableRow>
            )
        }

        return distributorRecords.map(record => (
            <TableRow key={record.baid} sx={{ backgroundColor: 'rgba(0,0,0,.05)' }}>
                <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{record.baid}</TableCell>
                <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{record.name}</TableCell>
                <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{record.status}</TableCell>
                <TableCell>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        sx={{ textTransform: 'capitalize', background: '#007bff' }}
                        onClick={() => handleViewInformation(record.baid)}
                        disabled={isLoading}>
                        xem thông tin
                    </Button>
                </TableCell>
            </TableRow>
        ))
    }

    return (
        <PageContainer>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="flex-start" spacing={2} mb={3}>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                    Danh sách nhà phân phối
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: 'fit-content' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        tìm kiếm :
                    </Typography>
                    <TextField
                        size="small"
                        value={searchValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        helperText={errorMessage || ''}
                        FormHelperTextProps={{ sx: { mt: 0.5, color: 'error.main', fontWeight: 600 } }}
                        sx={{ minWidth: 220, maxWidth: 400 }}
                        disabled={isLoading}
                    />
                </Stack>
            </Stack>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <Table
                    sx={{
                        '& .MuiTableCell-root': {
                            borderBottom: 'none',
                            textAlign: 'center',
                        }
                    }}
                    size="medium">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #dee2e6' }}>Mã nhà phân phối</TableCell>
                            <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #dee2e6' }}>Họ và tên</TableCell>
                            <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #dee2e6' }}>Tình trạng Nhà Phân Phối</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Hoạt Động</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderTableContent()}
                    </TableBody>
                </Table>
            </TableContainer>
        </PageContainer>
    )
}

export default DistributorLayout
